import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { X, FileCheck } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

interface CAROClauseIXChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (responses: Record<string, string>) => void;
  initialResponses?: Record<string, string>;
}

interface Question {
  id: string;
  text: string;
  dependsOn?: string;
  showIf?: string;
}

const questions: Question[] = [
  {
    id: "q1",
    text: "Has the company defaulted in repayment of loans or borrowings or interest thereon to any lender?"
  },
  {
    id: "q2",
    text: "Has the loan rescheduling/restructuring been approved by the concerned lenders during the year?",
    dependsOn: "q1",
    showIf: "yes"
  },
  {
    id: "q3",
    text: "Does the company have any loans repayable on demand?"
  },
  {
    id: "q4",
    text: "Has the company been declared a wilful defaulter by any bank or financial institution or government or government authority?"
  },
  {
    id: "q5",
    text: "Has the company taken any term loan?"
  },
  {
    id: "q6",
    text: "Were they applied for the purpose for which they were obtained?",
    dependsOn: "q5",
    showIf: "yes"
  },
  {
    id: "q7",
    text: "Are there any loans taken near the period-end due to which they remain unutilized?",
    dependsOn: "q5",
    showIf: "yes"
  },
  {
    id: "q8",
    text: "Has the company raised funds on short-term basis?"
  },
  {
    id: "q9",
    text: "Have the funds raised on short-term basis been used for long-term purposes?",
    dependsOn: "q8",
    showIf: "yes"
  },
  {
    id: "q10",
    text: "Does the company have any subsidiary, associate or joint venture?"
  },
  {
    id: "q11",
    text: "Has the company taken any funds from any entity or person during the year or has any unutilized funds raised through borrowings/issue of securities in previous year?",
    dependsOn: "q10",
    showIf: "yes"
  },
  {
    id: "q12",
    text: "Has the company made investment in or given any new loans or advances to its subsidiary, associate or joint venture during the year out of the funds raised or unutilized above?",
    dependsOn: "q11",
    showIf: "yes"
  },
  {
    id: "q13",
    text: "Has the company raised loans during the year?"
  },
  {
    id: "q14",
    text: "Have the loans been raised on the pledge of securities held in its subsidiaries, joint ventures or associate companies?",
    dependsOn: "q13",
    showIf: "yes"
  },
  {
    id: "q15",
    text: "Has the company defaulted in the repayment of any such loans?",
    dependsOn: "q14",
    showIf: "yes"
  }
];

export default function CAROClauseIXChecklistModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialResponses = {}
}: CAROClauseIXChecklistModalProps) {
  const { isArchivalMode } = useArchivalMode();
  const [responses, setResponses] = useState<Record<string, string>>(initialResponses);

  // Update responses when initialResponses changes (when modal opens with new data)
  React.useEffect(() => {
    if (isOpen && initialResponses) {
      setResponses(initialResponses);
    }
  }, [isOpen, initialResponses]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const shouldShowQuestion = (question: Question): boolean => {
    if (!question.dependsOn) return true;
    
    const dependentResponse = responses[question.dependsOn];
    return dependentResponse === question.showIf;
  };

  const handleSave = () => {
    onSave(responses);
    onClose();
  };

  const handleClose = () => {
    setResponses({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-4xl max-h-[90vh] border-white/10 bg-black/80 text-white shadow-2xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileCheck className="h-5 w-5 text-[#4da3ff]" />
            <CardTitle className="text-lg">
              CARO Clause (ix) - Borrowings
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {questions.map((question, index) => {
              const isVisible = shouldShowQuestion(question);
              
              if (!isVisible) return null;

              return (
                <div key={question.id} className="space-y-3">
                  <p className="text-sm text-white/90">
                    {question.text}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="yes"
                        checked={responses[question.id] === "yes"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="no"
                        checked={responses[question.id] === "no"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">No</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>

        <div className="flex justify-end gap-3 p-6 border-t border-white/10">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white/20 text-white/80 hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
            disabled={isArchivalMode}
          >
            Save Responses
          </Button>
        </div>
      </Card>
    </div>
  );
}
